import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import DoNotDisturbIcon from '@mui/icons-material/DoNotDisturbOn'

import { Appliance, ListApplianceSortableField } from 'common/api/v1/types'
import {
  getApplianceOwnerId,
  isEditableAppliance,
  isEditableGroup,
  isEditableRegion,
  pluralize,
  useApplianceSelector,
  usePageParamsFilteredSelector,
  useUser,
} from '../../utils'
import { AppDispatch, useRoutes } from '../../store'
import { getProductName } from 'common/api/v1/helpers'
import Table, { TableConfig } from '../common/Table'
import { MissingContent } from '../common/MissingContent'
import Wrapper from '../common/Wrapper'
import { Link } from '../common/Link'
import { NonSubscribingAutoUpdatingApplianceHealthIndicator } from '../common/Indicator'
import Tooltip from '../common/Tooltip'
import { EnrichedApplianceWithOwner } from '../../api/nm-types'
import CommonActions from './CommonActions'
import { AddEdgeConnectDialog } from './AddEdgeConnectDialog'
import {
  registerApplianceObserver,
  removeAppliance,
  restartAppliance,
  unregisterApplianceObserver,
} from '../../redux/actions/applianceActions'
import { useDispatch } from 'react-redux'
import { ActionMenu } from '../common/Table/ActionMenu'
import DeleteIcon from '@mui/icons-material/Delete'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import SettingsIcon from '@mui/icons-material/Settings'
import { useConfirmationDialog } from '../common/ConfirmDialog'
import { RemoveDialog } from './Edit/RemoveDialog'
import { ApplianceProxyButton } from '../common/ApplianceProxyButton'

export const ListView = () => {
  const routes = useRoutes()
  const dispatch = useDispatch<AppDispatch>()
  const { appliances, total, loading } = usePageParamsFilteredSelector(useApplianceSelector)
  const user = useUser()
  const navigate = useNavigate()
  const [selected, setSelected] = useState<Array<Appliance['id']>>([])
  const [showAddEdgeConnectDialog, setShowAddEdgeConnectDialog] = useState(false)
  const { showConfirmation, hideConfirmation } = useConfirmationDialog()

  const applianceIds = appliances.map((a) => a.id)
  useEffect(() => {
    dispatch(registerApplianceObserver({ applianceIds }))
    return () => {
      dispatch(unregisterApplianceObserver({ applianceIds }))
    }
  }, [JSON.stringify(applianceIds)])

  const handleSelect = (id: Appliance['id']) => {
    if (selected.includes(id)) {
      setSelected([...selected.filter((item) => item !== id)])
    } else {
      setSelected([...selected, id])
    }
  }

  const handleSelectAll = () => {
    if (selected.length === appliances.length) {
      setSelected([])
    } else {
      setSelected(appliances.reduce<Array<Appliance['id']>>((acc, { id }) => acc.concat(id), []))
    }
  }

  const tableConfig: TableConfig<EnrichedApplianceWithOwner, ListApplianceSortableField> = [
    {
      title: (
        <Checkbox
          indeterminate={selected.length > 0 && selected.length < appliances.length}
          checked={selected.length === appliances.length}
          onChange={() => {
            handleSelectAll()
          }}
          inputProps={{ 'aria-label': 'select all appliances' }}
          data-test-id={'select-all'}
        />
      ),
      getValue: ({ id }) => (
        <Checkbox
          checked={selected.includes(id)}
          onChange={() => {
            handleSelect(id)
          }}
          onDoubleClick={(e) => e.stopPropagation()}
        />
      ),
      props: {
        padding: 'checkbox',
        noTypography: true,
      },
    },
    {
      title: 'status',
      getValue: ({ id, unhealthyAlarm, cordon }) => (
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <NonSubscribingAutoUpdatingApplianceHealthIndicator applianceId={id} />
          {unhealthyAlarm === 'off' && (
            <Tooltip title={'Status alarms are disabled for this appliance'}>
              <VisibilityOffIcon style={{ marginLeft: '5px' }} />
            </Tooltip>
          )}
          {cordon && (
            <Tooltip title={'This appliance is marked as cordoned - it is not available for provisioning services.'}>
              <DoNotDisturbIcon style={{ fontSize: '1.25em', marginLeft: unhealthyAlarm === 'off' ? '12px' : '4px' }} />
            </Tooltip>
          )}
        </div>
      ),
      sorting: { byParameter: ListApplianceSortableField.lastSeen },
      props: { noTypography: true },
    },
    {
      title: 'name',
      getValue: (appliance) => (
        <Link
          to={routes.appliancesUpdate({ id: appliance.id })}
          available={isEditableAppliance(user, getApplianceOwnerId(appliance))}
          underline="hover"
        >
          {appliance.name}
        </Link>
      ),
      sorting: {
        byParameter: ListApplianceSortableField.applianceName,
      },
    },
    {
      title: 'type',
      getValue: ({ type }) => getProductName(type),
      sorting: { byParameter: ListApplianceSortableField.applianceType },
    },
    {
      title: 'regions',
      getValue: (appliance) => (
        <>
          <Link
            available={isEditableRegion(user)}
            to={routes.regionsUpdate({ id: appliance.region.id })}
            underline="hover"
          >
            {appliance.region.name}
          </Link>
          {appliance.secondaryRegion ? (
            <>
              {', '}
              <Link
                available={isEditableRegion(user)}
                to={routes.regionsUpdate({ id: appliance.secondaryRegion.id })}
                underline="hover"
              >
                {appliance.secondaryRegion.name}
              </Link>
            </>
          ) : null}
        </>
      ),
      sorting: { byParameter: ListApplianceSortableField.regionName },
    },
    {
      title: 'owner',
      getValue: (values) => (
        <Link
          to={routes.groupsUpdate({ id: getApplianceOwnerId(values) })}
          underline="hover"
          available={values._owner && isEditableGroup(values._owner.id, user)}
        >
          {(values._owner && values._owner.name) || ''}
        </Link>
      ),
      sorting: { byParameter: ListApplianceSortableField.ownerGroupName },
    },
    {
      title: 'alarms',
      getValue: ({ id, alarms, name }) =>
        alarms.length ? (
          <Link to={routes.alarms({ applianceId: id, applianceName: name })} underline="hover">
            {pluralize(alarms.length, 'alarm')}
          </Link>
        ) : (
          'No alarms'
        ),
      sorting: { byParameter: ListApplianceSortableField.numActiveAlarms },
    },
    {
      getValue: (appliance) => {
        const hasApplianceAccess = isEditableAppliance(user, getApplianceOwnerId(appliance))
        if (!hasApplianceAccess) return null
        return (
          <>
            <ApplianceProxyButton user={user} appliance={appliance} />
            <ActionMenu
              options={[
                {
                  icon: <SettingsIcon />,
                  label: 'Edit',
                  href: routes.appliancesUpdate({ id: appliance.id }),
                },
                {
                  icon: <RestartAltIcon />,
                  label: 'Restart',
                  onClick: () => {
                    showConfirmation(
                      () => void dispatch(restartAppliance({ id: appliance.id, showSuccessSnackbar: true })),
                      'Restarting will affect all video streams on the appliance. After restart the appliance will run the latest software version. Do you want to continue?',
                    )
                  },
                },
                {
                  icon: <DeleteIcon />,
                  label: 'Remove',
                  isDestructive: true,
                  onClick: () => {
                    showConfirmation(
                      () => void dispatch(removeAppliance(appliance.id)),
                      <RemoveDialog appliance={appliance} hideConfirmDialog={hideConfirmation} />,
                      undefined,
                      { ok: { text: 'remove' } },
                    )
                  },
                },
              ]}
            />
          </>
        )
      },
      props: {
        align: 'right',
        noTypography: true,
      },
    },
  ]

  return (
    <Wrapper
      name="Appliances"
      actions={
        <Button
          variant="contained"
          color="primary"
          sx={{ marginLeft: 4 }}
          onClick={() => setShowAddEdgeConnectDialog(true)}
        >
          Add Edge Connect
        </Button>
      }
    >
      <CommonActions selected={selected} setSelected={setSelected} appliances={appliances} user={user} />
      <Table<EnrichedApplianceWithOwner>
        id="appliances"
        emptyMessageComponent={<MissingContent message="No appliances available" />}
        config={tableConfig}
        data={appliances}
        pending={loading}
        pagination={{ total, useUrlSearchParams: true }}
        rowProps={(appliance) => {
          const isEditable = isEditableAppliance(user, getApplianceOwnerId(appliance))
          const isItemSelected = Boolean(appliance.id && selected.includes(appliance.id))
          return {
            onDoubleClick: () => isEditable && void navigate(routes.appliancesUpdate({ id: appliance.id })),
            'aria-checked': isItemSelected,
            tabIndex: -1,
            selected: isItemSelected,
          }
        }}
      />
      <AddEdgeConnectDialog
        user={user}
        open={showAddEdgeConnectDialog}
        onClose={() => setShowAddEdgeConnectDialog(false)}
      />
    </Wrapper>
  )
}
